import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../../../components/ui/tabs';
import DeleteIntentModal from '../DeleteIntentModal/DeleteIntentModal';
import BotResponseTreeNode from '../../../types/BotBuilder/BotResponseTreeNode';
import Intent from '../../../types/BotBuilder/Intent';
import IntentTreeNode from '../../../types/BotBuilder/IntentTreeNode';
import CollectVariableTreeNode from '../../../types/BotBuilder/CollectVariableTreeNode';
import IntentNodeSettings from './IntentNodeSettings/IntentNodeSettings';
import MainIntentSettings from './MainIntentSettings/MainIntentSettings';
import MessageBlockTreeNode from '../../../types/BotBuilder/MessageBlockTreeNode';
import ResponseNodeSettings from './ResponseNodeSettings/ResponseNodeSettings';
import VariablesSettings from './VariablesSettings/VariablesSettings';
import IntentFormData from '../../../types/BotBuilder/IntentFormData';
import Button from '../../../components/Button/Button';
import { cn } from '../../../lib/utils';

interface BotBuilderSidebarProps {
  intent: Intent | null;
  onUpdateIntent: (intent: Intent) => void;
  intentHasChanged: boolean;
  isNewIntent: boolean;
  onSaveIntent: (formData?: IntentFormData) => Promise<void>;
  selectedNode: BotResponseTreeNode | null;
  selectionTick: number;
  onSelectedNodeUpdate: (node: BotResponseTreeNode) => void;
  onFormDataChanged?: (hasChanged: boolean) => void;
  formData: IntentFormData;
  setFormData: (formData: IntentFormData) => void;
  isLocked?: boolean;
}

type TabOptions = 'general' | 'triggers' | 'variables';

function BotBuilderSidebar({
  intent,
  onUpdateIntent,
  intentHasChanged,
  isNewIntent,
  onSaveIntent,
  selectedNode,
  selectionTick,
  onSelectedNodeUpdate,
  onFormDataChanged,
  formData,
  setFormData,
  isLocked = false
}: Readonly<BotBuilderSidebarProps>): React.ReactElement {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tab, setTab] = useState<TabOptions>('general');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [isSavingIntent, setIsSavingIntent] = useState<boolean>(false);
  const [formDataHasChanged, setFormDataHasChanged] = useState<boolean>(false);

  useEffect(() => {
    const hasChanged =
      intent &&
      (formData.name !== intent.name ||
        formData.description !== intent.description ||
        formData.category !== intent.category ||
        formData.safeguarding !== intent.safeguarding ||
        formData.safeguardingRecipient !== intent.safeguardingRecipient ||
        formData.locked !== intent.locked);
    const changed = !!hasChanged;
    setFormDataHasChanged(changed);
    if (onFormDataChanged) {
      onFormDataChanged(changed);
    }
  }, [formData, intent, onFormDataChanged]);

  const lastManualTab = useRef<TabOptions>('general');

  useEffect(() => {
    if (!selectedNode) {
      setTab(lastManualTab.current);
      return;
    }
    if (selectedNode.content instanceof IntentTreeNode) {
      setTab('triggers');
    } else if (selectedNode.content instanceof CollectVariableTreeNode) {
      setTab('variables');
    } else if (selectedNode.content instanceof MessageBlockTreeNode) {
      setTab('general');
    }
  }, [selectedNode, selectionTick]);

  const handleTabChange = (nextTab: string): void => {
    const resolvedTab = nextTab as TabOptions;
    lastManualTab.current = resolvedTab;
    setTab(resolvedTab);
  };

  const toggleDrawerOpen = (): void => setSidebarOpen(!sidebarOpen);

  const handleSaveIntentClick = async (): Promise<void> => {
    setIsSavingIntent(true);
    try {
      await onSaveIntent(formData);
    } finally {
      setIsSavingIntent(false);
    }
  };

  const handleRootNodeUpdate = (node: BotResponseTreeNode): void => {
    if (!intent) return;
    onUpdateIntent({ ...intent, rootNode: node });
  };

  const messageBlockNode = useMemo(
    () =>
      selectedNode && selectedNode.content instanceof MessageBlockTreeNode
        ? selectedNode
        : null,
    [selectedNode]
  );

  const tabTriggerClass =
    'flex-1 rounded-md px-3 py-1.5 text-sm font-normal transition-none ' +
    'text-white/60 hover:text-white ' +
    'data-[state=active]:bg-white data-[state=active]:text-brand-main';

  return (
    <div className='relative z-[5] flex pointer-events-none'>
      <button
        type='button'
        aria-label='Toggle settings panel'
        onClick={toggleDrawerOpen}
        className={cn(
          'absolute top-0 z-[3] flex h-10 w-10 items-center justify-center',
          'rounded-r-full bg-brand-main text-white/70',
          'pointer-events-auto transition-colors hover:text-white',
          '-right-10'
        )}
      >
        <ChevronRight
          className={cn(
            'h-5 w-5 transition-transform duration-300',
            sidebarOpen ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>
      <aside
        className={cn(
          'relative z-[2] box-border overflow-hidden bg-brand-main',
          'transition-[width] duration-300',
          sidebarOpen ? 'w-[25rem]' : 'w-10'
        )}
      >
        <div
          className={cn(
            'box-border flex h-[calc(100vh-5rem)] flex-col pointer-events-auto transition-opacity duration-200',
            sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <Tabs
            value={tab}
            onValueChange={handleTabChange}
            className='flex min-h-0 flex-1 flex-col gap-0 pt-5'
          >
            <TabsList className='mx-5 w-auto gap-1 rounded-md bg-white/10 p-1'>
              <TabsTrigger value='general' className={tabTriggerClass}>
                General
              </TabsTrigger>
              <TabsTrigger value='triggers' className={tabTriggerClass}>
                Triggers
              </TabsTrigger>
              <TabsTrigger value='variables' className={tabTriggerClass}>
                Variables
              </TabsTrigger>
            </TabsList>

            <div className='flex-1 min-h-0 overflow-y-auto pl-5'>
              <TabsContent value='general' className='mt-0 pt-4 pb-6 pr-5'>
                <div className='mb-4 border-b border-white/10 pb-3'>
                  <h2 className='text-lg font-semibold text-white'>
                    Question settings
                  </h2>
                </div>
                <MainIntentSettings
                  intent={intent}
                  formData={formData}
                  updateFormData={setFormData}
                  isLocked={isLocked}
                />
                {messageBlockNode && (
                  <div className='mt-4 border-t border-white/10 pt-4'>
                    <ResponseNodeSettings
                      node={messageBlockNode}
                      onNodeUpdate={onSelectedNodeUpdate}
                      isLocked={isLocked}
                    />
                  </div>
                )}
              </TabsContent>

              <TabsContent value='triggers' className='mt-0 pt-4 pb-6 pr-5'>
                <div className='mb-4 border-b border-white/10 pb-3'>
                  <h2 className='mb-2 text-lg font-semibold text-white'>
                    Triggers
                  </h2>
                  <p className='text-sm leading-relaxed text-white/60'>
                    Add phrases that will trigger this question.
                  </p>
                </div>
                {intent && (
                  <IntentNodeSettings
                    node={intent.rootNode}
                    onNodeUpdate={handleRootNodeUpdate}
                    isLocked={isLocked}
                  />
                )}
              </TabsContent>

              <TabsContent value='variables' className='mt-0 pt-4 pr-5'>
                <VariablesSettings isLocked={isLocked} />
              </TabsContent>
            </div>
          </Tabs>

          {modalOpen && intent && (
            <DeleteIntentModal
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              intent={intent}
            />
          )}

          <div
            className={cn(
              'sticky bottom-0 flex w-full items-center gap-3',
              'border-t border-white/10 bg-brand-main px-5 py-4',
              'shadow-[0_-4px_12px_rgba(0,0,0,0.15)]'
            )}
          >
            {!isNewIntent && !isLocked && (
              <Button
                variant='destructive'
                size='full'
                className='flex-1'
                onClick={(): void => setModalOpen(true)}
              >
                Delete question
              </Button>
            )}
            {!isLocked && (
              <Button
                loading={isSavingIntent}
                variant='default'
                size='full'
                className='flex-1'
                onClick={handleSaveIntentClick}
                disabled={
                  !isNewIntent && !intentHasChanged && !formDataHasChanged
                }
              >
                {isNewIntent ? 'Create question' : 'Save changes'}
              </Button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

export default BotBuilderSidebar;
